import type {
	EventReanalysis,
	EventReanalysisMeasurement,
	EventReanalysisSummarySnapshot,
	EventReanalysisView,
} from "@sitescene/types";

import { EventReanalysisStatus } from "@sitescene/types";
import { useState } from "react";
import { Link } from "react-router";

import { AnchoredObservations } from "@/components/anchored-observations";
import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button } from "@/components/field";
import { plausibilityVerificationPath } from "@/constants/routes";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";
import {
	classNames,
	nameClass,
} from "@/features/reanalysis/describe-reanalysis";
import {
	canApplyAnyway,
	isReanalysisOpen,
	isReanalysisOverdue,
	type ReanalysisActionInput,
	revertableId,
	useReanalysisAction,
} from "@/features/reanalysis/use-reanalysis";
import { apiErrorMessage } from "@/features/scene-facts/use-scene-proposals";
import {
	formatPercent,
	formatSeverityChange,
	formatWhen,
} from "@/pages/plausibility/format";
import { refId } from "@/utils/ids";

import {
	KIND_LABELS,
	OUTCOME_LABELS,
	SKIP_REASON_LABELS,
	SOURCE_LABELS,
	STATUS_LABELS,
} from "./format";

type Names = ReadonlyMap<string, string>;

/**
 * Every reanalysis of the event, newest first: what staff said, what it did
 * to the event, and the evidence each detection's run weighed. Revert sits on
 * the newest applied result only; Apply anyway on a result the footage did
 * not support.
 */
export const ReanalysisHistory = ({
	checkedAt,
	eventId,
	isFetching,
	view,
}: {
	/** When the view was last fetched (epoch ms). */
	checkedAt: number;
	eventId: string;
	isFetching: boolean;
	view: EventReanalysisView;
}) => {
	const taxonomy = useTaxonomy();
	const names = classNames(taxonomy.data);
	const { reanalyses } = view;
	const revertId = revertableId(reanalyses);
	// A run in flight is about to rewrite the event; acting now would race it.
	const busy = reanalyses.some(isReanalysisOpen);

	return (
		<DetailPanel title="Reanalysis history">
			{reanalyses.length === 0 ? (
				<p className="text-tertiary text-sm">
					This event has not been reanalyzed yet.
				</p>
			) : (
				<ol className="flex flex-col gap-3">
					{reanalyses.map((reanalysis) => (
						<li key={reanalysis.id}>
							<ReanalysisCard
								busy={busy}
								canRevert={reanalysis.id === revertId}
								checkedAt={checkedAt}
								eventId={eventId}
								isFetching={isFetching}
								names={names}
								reanalysis={reanalysis}
							/>
						</li>
					))}
				</ol>
			)}
		</DetailPanel>
	);
};

const ReanalysisCard = ({
	busy,
	canRevert,
	checkedAt,
	eventId,
	isFetching,
	names,
	reanalysis: r,
}: {
	busy: boolean;
	canRevert: boolean;
	checkedAt: number;
	eventId: string;
	isFetching: boolean;
	names: Names;
	reanalysis: EventReanalysis;
}) => {
	// Polling stops once overdue, so the card says so and offers a manual
	// check. That asks the server, which closes a run stuck past the worker's
	// retries, so an Apply anyway that died can be pressed again.
	const overdue = isReanalysisOverdue(r, checkedAt);
	const check = useReanalysisAction(eventId);
	const applyAnyway = canApplyAnyway(r);
	const stamps = [
		r.appliedAt && `Applied ${formatWhen(r.appliedAt)}.`,
		r.forced &&
			`Forced by ${r.forced.byName ?? r.forced.by} ${formatWhen(r.forced.at)}.`,
		r.reverted &&
			`Reverted by ${r.reverted.byName ?? r.reverted.by} ${formatWhen(r.reverted.at)}.`,
	].filter(Boolean);

	return (
		<section
			aria-label={`Reanalysis requested ${formatWhen(r.requestedAt)}`}
			className="border-secondary rounded-md border p-3"
		>
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<Badge label={STATUS_LABELS[r.status]} value={r.status} />
				{r.outcome && (
					<Badge label={OUTCOME_LABELS[r.outcome]} value={r.outcome} />
				)}
				<span className="text-primary font-medium">{KIND_LABELS[r.kind]}</span>
				<span className="text-secondary">
					— staff said {nameClass(r.targetClassId, names)}
				</span>
				{r.forced && (
					<span className="text-warning-primary text-xs font-semibold">
						Applied by staff override
					</span>
				)}
			</div>
			<p className="text-tertiary mt-1 text-xs">
				Requested by {r.requestedByName ?? r.requestedBy}{" "}
				{formatWhen(r.requestedAt)} via {SOURCE_LABELS[r.source]}.
			</p>

			{isReanalysisOpen(r) && (
				<div className="mt-2 flex flex-wrap items-center gap-3" role="status">
					<p className="text-secondary text-sm">
						{overdue
							? "Still no result after 15 minutes, so the run may have been lost."
							: r.status === EventReanalysisStatus.Queued
								? "Queued — waiting for the worker. This page refreshes on its own."
								: "Running — the models are reading the clip. This page refreshes on its own."}
					</p>
					{overdue && (
						<Button
							disabled={check.isPending || isFetching}
							onClick={() =>
								check.mutate({ action: "check", reanalysisId: r.id })
							}
							type="button"
							variant="secondary"
						>
							Check again
						</Button>
					)}
					{check.error && (
						<span className="text-error-primary text-sm" role="alert">
							{apiErrorMessage(check.error)}
						</span>
					)}
				</div>
			)}
			{r.status === EventReanalysisStatus.Failed && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					The run failed{r.error ? `: ${r.error}` : "."}
				</p>
			)}
			{r.status === EventReanalysisStatus.Completed && r.error && (
				<p className="text-warning-primary mt-2 text-sm" role="alert">
					{r.error}
				</p>
			)}

			<p className="text-tertiary mt-3 text-xs font-medium">
				What actually happened
			</p>
			<p className="text-primary mt-1 whitespace-pre-wrap text-sm italic">
				“{r.whatHappened}”
			</p>
			{r.whatWasWrong && (
				<>
					<p className="text-tertiary mt-2 text-xs font-medium">
						What the event got wrong (internal)
					</p>
					<p className="text-primary mt-1 whitespace-pre-wrap text-sm">
						{r.whatWasWrong}
					</p>
				</>
			)}

			<BeforeAfter after={r.after} before={r.before} />
			{stamps.length > 0 && (
				<p className="text-tertiary mt-2 text-xs">{stamps.join(" ")}</p>
			)}

			{r.measurements.map((m) => (
				<MeasurementRun
					key={refId(m.measurement)}
					measurement={m}
					names={names}
				/>
			))}

			{(canRevert || applyAnyway) && (
				<div className="mt-3 flex flex-col gap-2">
					{canRevert && (
						<ConfirmAction
							action="revert"
							busy={busy}
							confirmLabel="Confirm revert"
							eventId={eventId}
							label="Revert"
							prompt="Put back what the event said before this correction?"
							reanalysisId={r.id}
						/>
					)}
					{applyAnyway && (
						<ConfirmAction
							action="force"
							busy={busy}
							confirmLabel="Yes, apply anyway"
							eventId={eventId}
							label="Apply anyway"
							prompt="The footage didn't support this correction. Applying it changes the event anyway, and the change won't reflect what the models saw."
							reanalysisId={r.id}
						/>
					)}
				</div>
			)}
		</section>
	);
};

interface ChangeRow {
	after: string;
	before: string;
	field: string;
}

/** What the event said when the request was made, beside what it says since. */
const BeforeAfter = ({
	after,
	before,
}: {
	after: EventReanalysisSummarySnapshot | undefined;
	before: EventReanalysisSummarySnapshot;
}) => {
	const rows: ChangeRow[] = [
		{ after: after?.title ?? "—", before: before.title ?? "—", field: "Title" },
		{
			after: after ? String(after.severity) : "—",
			before: String(before.severity),
			field: "Severity",
		},
		{
			after: after?.description ?? "—",
			before: before.description ?? "—",
			field: "Description",
		},
	];
	const columns: Column<ChangeRow>[] = [
		{ header: "", key: "field", render: (row) => row.field },
		{
			header: "Before",
			key: "before",
			render: (row) => <span className="text-tertiary">{row.before}</span>,
		},
		{ header: "After", key: "after", render: (row) => row.after },
	];
	return (
		<div className="mt-3">
			<DataTable columns={columns} rowKey={(row) => row.field} rows={rows} />
			{!after && (
				<p className="text-tertiary mt-1 text-xs">
					This reanalysis has not changed the event.
				</p>
			)}
		</div>
	);
};

/** One detection's run: the class it tested, the evidence, and the answer. */
const MeasurementRun = ({
	measurement: m,
	names,
}: {
	measurement: EventReanalysisMeasurement;
	names: Names;
}) => {
	const name = (classId: string) => nameClass(classId, names);
	const top = m.blind?.ranking?.topClassId;
	const topSupport = m.blind?.ranking?.ranked.find(
		(r) => r.classId === top,
	)?.support;
	const blind = top
		? `${name(top)}${topSupport ? ` — ${topSupport}` : ""}`
		: m.blind === null || m.blind?.ranking === null
			? "No usable answer"
			: "—";

	return (
		<div className="border-secondary mt-3 border-t pt-3">
			<h4 className="text-primary mb-2 text-sm font-semibold">
				Detection{" "}
				<code className="text-xs font-normal">{refId(m.measurement)}</code>
			</h4>
			<KeyValueList
				items={[
					{ label: "Candidate class", value: name(m.candidateClassId) },
					{
						label: "Look-alikes",
						value: m.confuserIds.map(name).join(", ") || "—",
					},
					{ label: "Decision", value: <Badge value={m.decision} /> },
					{ label: "Posterior", value: formatPercent(m.posterior) },
					{
						label: "Proposed severity",
						value:
							m.proposedSeverity === undefined
								? "—"
								: formatSeverityChange(m.originalSeverity, m.proposedSeverity),
					},
					{
						label: "Supported",
						value: m.supported === undefined ? "—" : m.supported ? "Yes" : "No",
					},
					...(m.skipReason
						? [{ label: "Skipped", value: SKIP_REASON_LABELS[m.skipReason] }]
						: []),
					{
						label: "Blind pass",
						value: m.blindReusedFrom ? (
							<span>
								{blind} ·{" "}
								<Link
									className="underline"
									to={plausibilityVerificationPath(refId(m.blindReusedFrom))}
								>
									reused from pipeline verification
								</Link>
							</span>
						) : (
							blind
						),
					},
				]}
			/>
			{m.anchored && (
				<div className="mt-3">
					<p className="text-tertiary mb-1 text-xs font-medium">
						Anchored pass ({m.anchored.model})
					</p>
					<AnchoredObservations
						contributions={m.likelihood?.perFeature}
						pass={m.anchored}
					/>
				</div>
			)}
			{m.anchored === null && (
				<p className="text-tertiary mt-2 text-xs">
					The anchored pass returned nothing usable.
				</p>
			)}
		</div>
	);
};

/** Asks once more before acting, saying what the action does to the event. */
const ConfirmAction = ({
	action,
	busy,
	confirmLabel,
	eventId,
	label,
	prompt,
	reanalysisId,
}: ReanalysisActionInput & {
	busy: boolean;
	confirmLabel: string;
	eventId: string;
	label: string;
	prompt: string;
}) => {
	const [confirming, setConfirming] = useState(false);
	const act = useReanalysisAction(eventId);

	if (!confirming) {
		return (
			<div>
				<Button
					disabled={busy}
					onClick={() => setConfirming(true)}
					type="button"
					variant="secondary"
				>
					{label}
				</Button>
			</div>
		);
	}

	return (
		<div
			aria-label={`Confirm ${label.toLowerCase()}`}
			className="flex flex-wrap items-center gap-2"
			role="group"
		>
			<span className="text-secondary text-sm">{prompt}</span>
			<Button
				disabled={act.isPending || busy}
				onClick={() =>
					act.mutate(
						{ action, reanalysisId },
						{ onSuccess: () => setConfirming(false) },
					)
				}
				type="button"
			>
				{confirmLabel}
			</Button>
			<Button
				onClick={() => setConfirming(false)}
				type="button"
				variant="secondary"
			>
				Cancel
			</Button>
			{act.error && (
				<span className="text-error-primary text-sm" role="alert">
					{apiErrorMessage(act.error)}
				</span>
			)}
		</div>
	);
};
